import { createClient } from '@supabase/supabase-js';
import { captureSignupConfirmTokensFromLocation } from './authEmailConfirm';
import { capturePasswordRecoveryFromLocation } from './authPasswordRecovery';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Please check your .env.local file.');
  console.error('REACT_APP_SUPABASE_URL:', supabaseUrl ? 'Set' : 'Missing');
  console.error('REACT_APP_SUPABASE_ANON_KEY:', supabaseAnonKey ? 'Set' : 'Missing');
  throw new Error('Missing Supabase environment variables. Please check your .env.local file.');
}

// Must run before createClient — detectSessionInUrl clears hash tokens.
captureSignupConfirmTokensFromLocation();
capturePasswordRecoveryFromLocation();

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Implicit so reset/confirm emails work on another device or browser.
    // PKCE stores a verifier on the client that requested the email.
    flowType: 'implicit',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'supabase.auth.token'
  }
});

// Debug: Log session storage on initialization
if (typeof window !== 'undefined') {
  console.log('🔧 Supabase client initialized with storage:', {
    storageType: 'localStorage',
    storageKey: 'supabase.auth.token',
    hasStorage: !!window.localStorage,
    storedValue: localStorage.getItem('supabase.auth.token') ? 'Found' : 'Not found'
  });
}

// Database types for TypeScript
export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'member' | 'trainer';
  timezone?: string;
  has_logged_in: boolean;
  country?: string;
  referral_source?: string;
  created_at: string;
  updated_at: string;
  /** `deleted` = account closed and anonymized; user must not access the app. */
  account_status?: 'active' | 'deleted';
  deleted_at?: string | null;
  /** Product intent for push; gate push senders with active device tokens. */
  push_notifications_enabled?: boolean;
  /** Promotional tips/offers (email + push). Opt-in; default false. */
  marketing_notifications_enabled?: boolean;
  /** Opaque public invite slug for `/invite/{code}`. */
  invite_code?: string | null;
  /** Invite code used at signup (attribution). */
  invite_code_used?: string | null;
  /** User who owned the invite code at signup. */
  invited_by_user_id?: string | null;
}

export interface TrainerProfile {
  id: string;
  user_id: string;
  location?: string;
  experience?: string;
  hourly_rate?: number;
  specialties?: string[];
  certifications?: string;
  bio?: string;
  avatar_url?: string;
  availability?: string;
  rating?: number;
  total_reviews?: number;
  created_at: string;
  updated_at: string;
}

export interface ClientProfile {
  id: string;
  user_id: string;
  fitness_goals?: string[];
  fitness_level?: 'beginner' | 'intermediate' | 'advanced';
  created_at: string;
  updated_at: string;
} 